import { FC, useMemo } from 'react'
import { Box, Typography } from '@mui/material'
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import theme from 'theme'
import { AssetBreakdown, AssetBreakdownBucket, AssetBreakdownDimension, AssetBreakdownKind } from 'models'
import { useThemeColors } from 'hooks/useThemeColors'
import { sanitizeAssetValue, sanitizeChartLabel } from 'lib/sanitizeAssetValue'
import OnyxiaTooltip from 'ui/components/tooltip/OnyxiaTooltip'
import { seriesColorAt } from '../utils/chartSeries'
import { bucketLabel } from '../utils/assetBreakdown'
import { ChartCardWrapper, ChartTooltipBox, StatSkeletonBar } from './statCard.styles'
import InlineSelect from './InlineSelect'

/* Figma's copy, naming the bar as the chart labels it: No value, not Unknown. */
const BREAKDOWN_HINT =
  'Shows the top 4–5 values. Remaining values are grouped as Other, while missing values appear as No value.'

const TICK_LABEL_LENGTH = 12

interface AxisTickProps {
  x?: number
  y?: number
  payload?: { value: string }
  fill: string
  fontSize: number
  fontWeight: number
  letterSpacing: number
  fontFamily: string
}

/* A label cut to fit its column shows the full text on hover. */
const AxisTick: FC<AxisTickProps> = ({
  x = 0,
  y = 0,
  payload,
  fill,
  fontSize,
  fontWeight,
  letterSpacing,
  fontFamily
}) => {
  const full = sanitizeAssetValue(payload?.value) ?? ''
  const shown = sanitizeChartLabel(full, TICK_LABEL_LENGTH)

  const label = (
    <g transform={`translate(${x},${y})`}>
      <text
        dy={12}
        textAnchor="middle"
        fill={fill}
        fontSize={fontSize}
        fontWeight={fontWeight}
        letterSpacing={letterSpacing}
        fontFamily={fontFamily}
      >
        {shown}
      </text>
    </g>
  )

  return shown === full ? (
    label
  ) : (
    <OnyxiaTooltip title={full} placement="top">
      {label}
    </OnyxiaTooltip>
  )
}

interface Props {
  title: string
  icon: JSX.Element
  unit: string
  breakdown: AssetBreakdown
  loading: boolean
  dimensions: AssetBreakdownDimension[]
  dimension: AssetBreakdownDimension
  onDimensionChange: (dimension: AssetBreakdownDimension) => void
  onSelectBucket: (bucket: AssetBreakdownBucket) => void
}

const EntitiesBreakdownCard: FC<Props> = ({
  title,
  icon,
  unit,
  breakdown,
  loading,
  dimensions,
  dimension,
  onDimensionChange,
  onSelectBucket
}) => {
  const getColor = useThemeColors()
  const fontFamily = theme.typography.fontFamily.primary
  /* Figma axis labels: text-secondary, 12/500 with 0.5 tracking on x, 14/500 on y. */
  const axisFill = getColor('devices-secondary-text')
  const tick = { fill: axisFill, fontSize: 12, fontWeight: 500, letterSpacing: 0.5, fontFamily }
  const yTick = { fill: axisFill, fontSize: 14, fontWeight: 500, fontFamily }

  /**
   * No value and Other sit out of the series ramp, so a value keeps its colour across dimension
   * changes; Other takes the design's accent pink and No value a neutral fill.
   */
  const rows = useMemo(() => {
    let valueIndex = -1

    return breakdown.buckets.map((bucket) => {
      if (bucket.kind === AssetBreakdownKind.Value) valueIndex += 1

      return {
        ...bucket,
        name: bucketLabel(bucket.label, bucket.kind),
        color:
          bucket.kind === AssetBreakdownKind.Value
            ? seriesColorAt(getColor, valueIndex)
            : bucket.kind === AssetBreakdownKind.Other
            ? getColor('cta')
            : getColor('chart-grid')
      }
    })
  }, [breakdown.buckets, getColor])

  return (
    <ChartCardWrapper>
      <Box className="stat-header">
        <Box className="stat-heading">
          <Box className="stat-icon">{icon}</Box>
          <Typography className="stat-title">{title}</Typography>
          <OnyxiaTooltip title={BREAKDOWN_HINT} placement="top">
            <Box component="button" type="button" className="stat-info" aria-label={BREAKDOWN_HINT}>
              <InformationCircleIcon />
            </Box>
          </OnyxiaTooltip>
        </Box>
        <InlineSelect
          label="By:"
          ariaLabel="Breakdown dimension"
          value={dimension.key}
          options={dimensions.map((item) => item.key)}
          optionLabel={(key) => dimensions.find((item) => item.key === key)?.label ?? key}
          onChange={(key) => {
            const next = dimensions.find((item) => item.key === key)
            if (next) onDimensionChange(next)
          }}
        />
      </Box>
      <Box className="chart-body">
        {loading && <StatSkeletonBar sx={{ width: '100%', height: '100%' }} />}

        {!loading && !rows.length && (
          <Typography className="chart-empty">No {unit.toLowerCase()} to break down for this window</Typography>
        )}

        {!loading && !!rows.length && (
          <ResponsiveContainer width="100%" height={220}>
            {/* The whole highlighted column is the target, not just the bar: a short bar is mostly empty. */}
            <BarChart
              data={rows}
              margin={{ top: 16, right: 8, bottom: 0, left: 0 }}
              barCategoryGap={16}
              style={{ cursor: 'pointer' }}
              onClick={(state) => {
                const index = state?.activeTooltipIndex

                if (typeof index === 'number' && breakdown.buckets[index]) onSelectBucket(breakdown.buckets[index])
              }}
            >
              <XAxis dataKey="name" tick={<AxisTick {...tick} />} axisLine={false} tickLine={false} interval={0} />
              <YAxis tick={yTick} axisLine={false} tickLine={false} width={48} allowDecimals={false} />
              <Tooltip
                cursor={{ fill: getColor('chart-cursor') }}
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null
                  const row = payload[0].payload as typeof rows[number]

                  return (
                    <ChartTooltipBox>
                      <span className="tooltip-swatch" style={{ background: row.color }} />
                      <span className="tooltip-label">{sanitizeChartLabel(row.name, 32)}</span>
                      <span className="tooltip-value">
                        {row.value.toLocaleString()} {unit}
                      </span>
                    </ChartTooltipBox>
                  )
                }}
              />
              <Bar dataKey="value" radius={[4, 4, 0, 0]} barSize={56}>
                {/* A real value can read "Other" too, so the kind is what tells the bars apart. */}
                {rows.map((row) => (
                  <Cell key={`${row.kind}-${row.name}`} fill={row.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </Box>
    </ChartCardWrapper>
  )
}

export default EntitiesBreakdownCard
